import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { GetMyPreferencesDocument, UpdatePreferencesDocument } from "#app/lib/data";

import type { WorkspaceWorkflow as WorkflowData } from "#app/lib/data";

export function useWorkflowOrder(workflows: WorkflowData[]) {
  const { data: prefsData } = useQuery(GetMyPreferencesDocument, {
    fetchPolicy: "cache-first",
  });
  const [updatePreferences] = useMutation(UpdatePreferencesDocument);

  const savedOrder: string[] | null =
    prefsData?.myPreferences?.workflowOrder ?? null;

  // Local override — set on drag, cleared when savedOrder catches up
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  const workflowOrder = localOrder ?? savedOrder;

  const sortedWorkflows = useMemo(() => {
    if (!workflowOrder || workflowOrder.length === 0) return workflows;
    const orderMap = new Map(workflowOrder.map((id, i) => [id, i]));
    return [...workflows].sort((a, b) => {
      const ai = orderMap.get(a.workflowId) ?? Infinity;
      const bi = orderMap.get(b.workflowId) ?? Infinity;
      return ai - bi;
    });
  }, [workflows, workflowOrder]);

  const workflowIds = useMemo(
    () => sortedWorkflows.map((wf) => wf.workflowId),
    [sortedWorkflows],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = workflowIds.indexOf(active.id as string);
      const newIndex = workflowIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const newOrder = arrayMove(workflowIds, oldIndex, newIndex);
      setLocalOrder(newOrder);
      updatePreferences({
        variables: { input: { workflowOrder: newOrder } },
        onCompleted: () => setLocalOrder(null),
        onError: () => setLocalOrder(null),
      });
    },
    [workflowIds, updatePreferences],
  );

  return { sortedWorkflows, workflowIds, handleDragEnd };
}
